// Sprint 3 Story 4: Externe-Gäste-Section in den Projekt-Einstellungen.
// Liste aller Invites + neuer Invite + Revoke + Copy-Link.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Copy, Plus, Trash2, UserPlus } from "lucide-preact";
import { api } from "../lib/api";
import { toastError, toastSuccess } from "../lib/toast";
import { confirmDialog } from "../lib/confirm";
import { useUsers } from "../store/users";

import { Button } from "./ui/Button";

interface InviteDTO {
  id: number;
  project_id: number;
  email: string;
  permission: "read" | "edit";
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_by: number;
  created_by_name: string | null;
  created_at: string;
  note: string | null;
  one_time_token?: string;
}

function inviteUrl(token: string): string {
  return `${window.location.origin}/external?token=${encodeURIComponent(token)}`;
}

export function ExternalGuestsSection({ projectId }: { projectId: number }): preact.JSX.Element {
  const [items, setItems] = useState<InviteDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState<"read" | "edit">("read");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recentlyCreated, setRecentlyCreated] = useState<InviteDTO | null>(null);
  // Ohne diesen Zustand sah ein fehlgeschlagener Abruf genau wie "keine Gaeste
  // eingeladen" aus — plausibel und darum unentdeckt. api() toastet nur
  // Mutationen (MUTATION_METHODS in lib/api.ts), dieser GET also nicht.
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = (): void => setReloadTick((t) => t + 1);

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    void api<InviteDTO[]>(`/api/projects/${projectId}/invites`).then(
      (rows) => {
        if (cancelled) return;
        setItems(rows);
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setLoadError(true);
        setLoading(false);
        toastError("Externe Gäste konnten nicht geladen werden.");
      }
    );
    return () => {
      cancelled = true;
    };
    // `reloadTick` haengt drin, damit „Erneut laden" den Abruf wirklich neu startet.
  }, [projectId, reloadTick]);

  const submit = async (): Promise<void> => {
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const created = await api<InviteDTO>(`/api/projects/${projectId}/invites`, {
        method: "POST",
        body: {
          email: email.trim(),
          permission,
          note: note.trim() || undefined,
        },
      });
      setItems((prev) => [created, ...prev]);
      setRecentlyCreated(created);
      setEmail("");
      setNote("");
      setShowAdd(false);
      toastSuccess(`Einladung an ${created.email} verschickt.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Einladen fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (id: number): Promise<void> => {
    const ok = await confirmDialog({
      title: "Einladung widerrufen",
      message: "Externer hat sofort keinen Zugriff mehr. Wirklich widerrufen?",
      confirmLabel: "Widerrufen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api(`/api/invites/${id}`, { method: "DELETE" });
      setItems((prev) =>
        prev.map((x) =>
          x.id === id ? { ...x, revoked_at: new Date().toISOString() } : x
        )
      );
      toastSuccess("Einladung widerrufen.");
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Widerrufen fehlgeschlagen");
    }
  };

  const active = items.filter((i) => !i.revoked_at);

  // Bereits bekannte Gaeste (aus anderen Projekten) zur Auswahl anbieten —
  // vorher gab es nur das freie E-Mail-Feld, ein vorhandener Gast war nicht
  // auswaehlbar. Wer hier schon eine aktive Einladung hat, faellt raus.
  const users = useUsers();
  const knownGuests = useMemo(() => {
    const invited = new Set(active.map((i) => i.email.toLowerCase()));
    const q = email.trim().toLowerCase();
    return users.items
      .filter((u) => u.is_external && !invited.has(u.email.toLowerCase()))
      .filter(
        (u) =>
          !q ||
          u.name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
      );
  }, [users.items, active, email]);

  return (
    <section class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4">
      <header class="flex items-center justify-between gap-2 mb-3">
        <h3 class="text-sm font-semibold text-[color:var(--color-on-surface)]">
          Externe Gäste
        </h3>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          class="inline-flex items-center gap-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
        >
          <UserPlus size={14} aria-hidden="true" />
          Einladen
        </button>
      </header>

      {recentlyCreated?.one_time_token && (
        <div class="mb-3 rounded border border-[color:var(--color-primary)]/30 bg-[color:var(--color-primary-container)]/30 p-3 text-xs">
          <p class="font-medium text-[color:var(--color-on-surface)] mb-1">
            Einladungslink für {recentlyCreated.email}
          </p>
          <code class="block break-all text-label text-[color:var(--color-on-surface-variant)] mb-2">
            {inviteUrl(recentlyCreated.one_time_token)}
          </code>
          <div class="flex gap-2">
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(
                  inviteUrl(recentlyCreated.one_time_token!)
                );
                toastSuccess("Link kopiert.");
              }}
              class="inline-flex items-center gap-1 rounded bg-[color:var(--color-primary)] px-2 py-1 text-label text-[color:var(--color-on-primary)]"
            >
              <Copy size={14} />
              Link kopieren
            </button>
            <button
              type="button"
              onClick={() => setRecentlyCreated(null)}
              class="text-label text-[color:var(--color-on-surface-variant)] underline"
            >
              Ausblenden
            </button>
          </div>
          <p class="mt-2 text-label text-[color:var(--color-on-surface-variant)]">
            Wir haben den Link an die Email geschickt. Sicherheits-halber kannst
            du ihn auch direkt teilen. <strong>Wird nur einmal angezeigt.</strong>
          </p>
        </div>
      )}

      {showAdd && (
        <div class="mb-3 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] p-3 space-y-2">
          <input
            type="email"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            placeholder="E-Mail oder Name eines bekannten Gasts"
            class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-base sm:text-sm"
            aria-label="Email"
          />
          {knownGuests.length > 0 && (
            <div>
              <p class="mb-1 text-label text-[color:var(--color-on-surface-variant)]">
                Bekannte Gäste
              </p>
              <ul class="max-h-40 overflow-y-auto rounded border border-[color:var(--color-outline-variant)] divide-y divide-[color:var(--color-outline-variant)]/50 bg-[color:var(--color-surface-container-lowest)]">
                {knownGuests.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => setEmail(u.email)}
                      aria-pressed={email.trim().toLowerCase() === u.email.toLowerCase()}
                      class={`w-full min-w-0 px-2 py-1.5 text-left text-xs hover:bg-[color:var(--color-surface-container-low)] ${
                        email.trim().toLowerCase() === u.email.toLowerCase()
                          ? "bg-[color:var(--color-primary-container)]/40"
                          : ""
                      }`}
                    >
                      <span class="block truncate font-medium text-[color:var(--color-on-surface)]">
                        {u.name}
                      </span>
                      <span class="block truncate text-label text-[color:var(--color-on-surface-variant)]">
                        {u.email}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div class="flex items-center gap-3 text-xs">
            <label class="inline-flex items-center gap-1">
              <input
                type="radio"
                checked={permission === "read"}
                onChange={() => setPermission("read")}
                class="accent-[color:var(--color-primary)]"
              />
              Nur lesen
            </label>
            <label class="inline-flex items-center gap-1">
              <input
                type="radio"
                checked={permission === "edit"}
                onChange={() => setPermission("edit")}
                class="accent-[color:var(--color-primary)]"
              />
              Bearbeiten
            </label>
          </div>
          <input
            type="text"
            value={note}
            onInput={(e) => setNote((e.target as HTMLInputElement).value)}
            placeholder="Optionale Notiz (intern)"
            class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-xs"
            maxLength={500}
          />
          <div class="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs"
            >
              Abbrechen
            </button>
            <Button
              variant="primary"
              size="xs"
              type="button"
              onClick={() => void submit()}
              disabled={!email.trim() || submitting}
            >
              <Plus size={14} />
              {submitting ? "Lege an…" : "Einladen"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      ) : loadError ? (
        <p class="text-xs text-[color:var(--color-error)]">
          Externe Gäste konnten nicht geladen werden.{" "}
          <button type="button" onClick={reload} class="underline">Erneut laden</button>
        </p>
      ) : active.length === 0 ? (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine externen Gäste eingeladen.
        </p>
      ) : (
        <ul class="divide-y divide-[color:var(--color-outline-variant)]">
          {active.map((inv) => {
            const expired = new Date(inv.expires_at).getTime() < Date.now();
            return (
              <li key={inv.id} class="flex items-center gap-3 py-2 text-xs">
                <div class="flex-1 min-w-0">
                  <p class="truncate font-medium text-[color:var(--color-on-surface)]">
                    {inv.email}
                  </p>
                  <p class="text-label text-[color:var(--color-on-surface-variant)]">
                    {inv.permission === "edit" ? "Bearbeiten" : "Nur lesen"} ·
                    {expired ? " abgelaufen am " : " bis "}
                    {new Date(inv.expires_at).toLocaleDateString("de-DE")}
                    {inv.accepted_at && " · aktiviert"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void revoke(inv.id)}
                  class="rounded p-1.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)]"
                  aria-label="Einladung widerrufen"
                  title="Einladung widerrufen"
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <p class="mt-3 text-label text-[color:var(--color-on-surface-variant)]">
        Externe Gäste sehen <strong>nur</strong> dieses Projekt. Zugriff endet
        nach 90 Tagen. Eine Verlängerung musst du off-Tool mit dem Externen
        absprechen und dann einen neuen Invite ausstellen.
      </p>
    </section>
  );
}
